import { apiFetch } from '@/lib/api';

export interface AIModelConfig {
  model: string;
  provider: 'gemini' | 'openai';
}

export interface ModelInfo {
  name: string;
  provider: string;
  description: string;
  costPer1M: string;
}

export interface AISettings {
  current: {
    default: AIModelConfig;
  };
  available: {
    default: Record<string, ModelInfo>;
  };
}

export interface MailSettings {
  provider: 'brevo' | 'smtp' | 'console' | null;
  brevo_api_key: string | null;
  smtp_host: string | null;
  smtp_port: number | null;
  smtp_secure: boolean | null;
  smtp_user: string | null;
  smtp_pass: string | null;
  from_email: string | null;
  from_name: string | null;
  configured: boolean;
}

export interface StorageSettings {
  provider: 'r2' | 's3' | 'hetzner' | 'minio' | null;
  endpoint: string | null;
  access_key_id: string | null;
  secret_access_key: string | null;
  bucket: string | null;
  public_base_url: string | null;
  region: string | null;
  configured: boolean;
}

export class SettingsService {
  /**
   * Get AI settings (current model and available options)
   */
  async getAISettings(): Promise<AISettings> {
    const response = await apiFetch('/api/settings/ai');
    if (!response.ok) {
      throw new Error('Failed to fetch AI settings');
    }
    const result = await response.json();
    return result.data;
  }

  /**
   * Update default AI model
   */
  async updateDefaultModel(model: string): Promise<void> {
    const response = await apiFetch('/api/settings/ai/default', {
      method: 'PUT',
      body: JSON.stringify({ model }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to update model' }));
      throw new Error(error.message || 'Failed to update model');
    }
  }

  /**
   * Update a generic setting
   */
  async updateSetting(key: string, value: string, options?: { description?: string; category?: string }): Promise<void> {
    const response = await apiFetch(`/api/settings/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value, ...options }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to update setting' }));
      throw new Error(error.message || 'Failed to update setting');
    }
  }

  // ─── Mail Settings ──────────────────────────────────────────────────

  /**
   * Get mail settings (secrets masked)
   */
  async getMailSettings(): Promise<MailSettings> {
    const response = await apiFetch('/api/settings/mail');
    if (!response.ok) {
      throw new Error('Failed to fetch mail settings');
    }
    const result = await response.json();
    return result.data;
  }

  /**
   * Update mail settings
   */
  async updateMailSettings(data: {
    provider: string;
    brevo_api_key?: string;
    smtp_host?: string;
    smtp_port?: number;
    smtp_secure?: boolean;
    smtp_user?: string;
    smtp_pass?: string;
    from_email?: string;
    from_name?: string;
  }): Promise<MailSettings> {
    const response = await apiFetch('/api/settings/mail', {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to update mail settings' }));
      throw new Error(error.message || 'Failed to update mail settings');
    }
    const result = await response.json();
    return result.data;
  }

  /**
   * Send a test email
   */
  async testMail(email: string): Promise<{ message: string; provider: string }> {
    const response = await apiFetch('/api/settings/mail/test', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to send test email' }));
      throw new Error(error.message || 'Failed to send test email');
    }
    const result = await response.json();
    return result.data;
  }

  // ─── Storage Settings ───────────────────────────────────────────────

  /**
   * Get storage settings (secrets masked)
   */
  async getStorageSettings(): Promise<StorageSettings> {
    const response = await apiFetch('/api/settings/storage');
    if (!response.ok) {
      throw new Error('Failed to fetch storage settings');
    }
    const result = await response.json();
    return result.data;
  }

  /**
   * Update storage settings.
   *
   * F-10: Use explicit update flags instead of the old '___UNCHANGED___' sentinel.
   * Set updateAccessKey: true + access_key_id to overwrite the access key.
   * Set updateAccessKey: false (or omit) to keep the existing encrypted value.
   * Same for updateSecretKey / secret_access_key.
   */
  async updateStorageSettings(data: {
    provider: string;
    endpoint: string;
    bucket: string;
    public_base_url?: string;
    region?: string;
    /** When true, write access_key_id to DB. When false/omitted, keep existing. */
    updateAccessKey?: boolean;
    access_key_id?: string;
    /** When true, write secret_access_key to DB. When false/omitted, keep existing. */
    updateSecretKey?: boolean;
    secret_access_key?: string;
  }): Promise<StorageSettings> {
    const response = await apiFetch('/api/settings/storage', {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to update storage settings' }));
      throw new Error(error.message || 'Failed to update storage settings');
    }
    const result = await response.json();
    return result.data;
  }

  /**
   * Test storage connection
   */
  async testStorage(): Promise<{ message: string; provider: string }> {
    const response = await apiFetch('/api/settings/storage/test', {
      method: 'POST',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to test storage connection' }));
      throw new Error(error.message || 'Failed to test storage connection');
    }
    const result = await response.json();
    return result.data;
  }
}
